import React from 'react'
import { useSelector } from 'react-redux'
import './KeyBoradDatePicker.scss'
import { CalendarIcon } from '../Icons/Icons'
import cn from 'classnames'
import { CustomDatePicker, CustomHeaderContainer, CustomPickersDay, CustomPickersTextField } from './dataPicker.configs'
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment'
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider'
import { PickersActionBar } from '@mui/x-date-pickers/PickersActionBar'
import moment from 'moment'

const ActionBar = (props) => {
  return <PickersActionBar {...props} />
}

const KeyBoardDatePicker = ({
  formik,
  keyBoardLabel,
  value,
  nameOfValue,
  minDate,
  maxDate,
  className,
  errorMessage,
  isInvalid,
  required,
  viewFormat = 'dense',
  ...rest
}) => {
  const userFormat = useSelector(({ user }) => user.settings.date_format)

  const getMomentValue = React.useCallback((val) => {
    if (!val) return null

    const m = moment(val)
    return m.isValid() ? m : null
  }, [])

  const [draftValue, setDraftValue] = React.useState(getMomentValue(value))
  const [open, setOpen] = React.useState(false)

  React.useEffect(() => {
    setDraftValue(getMomentValue(value))
  }, [getMomentValue, value])

  const setFormikValue = React.useCallback(
    (date) => {
      const nextValue = date ? getMomentValue(date) : null
      formik.setFieldTouched(nameOfValue, true, true)
      formik.setFieldValue(nameOfValue, nextValue)
    },
    [formik, getMomentValue, nameOfValue]
  )

  const setFormikDraftValue = React.useCallback(
    (date) => {
      const nextValue = date ? getMomentValue(date) : null
      formik.setFieldValue(nameOfValue, nextValue)
    },
    [formik, getMomentValue, nameOfValue]
  )

  const handleChangeData = (date) => {
    const next = date ? getMomentValue(date) : null
    setDraftValue(next)
    // Keep Formik in sync with manual typing, not only with picker "Accept".
    setFormikDraftValue(next)
  }

  const handleAccept = (date) => {
    const next = date ? getMomentValue(date) : null
    setDraftValue(next)
    setFormikValue(next)
    setOpen(false)
  }

  const handleClear = () => {
    setDraftValue(null)
    setFormikValue(null)
    setOpen(false)
  }

  const handleCancel = () => {
    setDraftValue(getMomentValue(value))
    setOpen(false)
  }

  const handleSetToday = (date) => {
    const next = date ? getMomentValue(date) : null
    setDraftValue(next)
    setFormikValue(next)
    setOpen(false)
  }

  return (
    <div className={cn('keyBoardDatePicker', className)}>
      {keyBoardLabel && (
        <p
          className={cn('keyBoardDatePickerLabel', {
            'keyBoardDatePickerLabel-disabled': rest?.disabled,
          })}
        >
          {keyBoardLabel} {required ? <span>*</span> : ''}
        </p>
      )}

      <LocalizationProvider dateAdapter={AdapterMoment} adapterLocale='en'>
        <CustomDatePicker
          open={open}
          onOpen={() => setOpen(true)}
          onClose={() => setOpen(false)}
          value={draftValue}
          onChange={handleChangeData}
          onAccept={handleAccept}
          slots={{
            openPickerIcon: CalendarIcon,
            calendarHeader: CustomHeaderContainer,
            day: CustomPickersDay,
            textField: CustomPickersTextField,
            actionBar: ActionBar,
          }}
          className={className}
          slotProps={{
            textField: {
              variant: 'outlined',
              error: isInvalid,
              helperText: isInvalid ? errorMessage : '',
            },
            actionBar: {
              actions: rest.today ? ['accept', 'today', 'clear', 'cancel'] : ['accept', 'clear', 'cancel'],
              onCancel: handleCancel,
              onClear: handleClear,
              onSetToday: handleSetToday,
            },
          }}
          format={userFormat}
          inputFormat={userFormat}
          formatDensity={viewFormat}
          minDate={minDate}
          maxDate={maxDate}
          {...rest}
        />
      </LocalizationProvider>
    </div>
  )
}

export default KeyBoardDatePicker
